/**
 * The two halves of "← Back returns to the view you left". See `utils/returnTo.ts`.
 *
 * Import from this file directly, not from the `hooks` barrel: the barrel pulls in
 * `services/api.ts`, which Jest cannot load.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildReturnState, resolveReturn } from '../utils/returnTo';

/**
 * Open a detail page and tell it where Back should return: this page's path and query
 * (its filters) plus this page's own return chain.
 *
 * Every link into a detail route must go through this. `returnTo.guard.test.ts` enforces it.
 */
export function useOpenWithReturn(): (to: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to: string) => navigate(to, { state: buildReturnState(location) }),
    [navigate, location]
  );
}

/**
 * Where "← Back" goes on a detail page: the view that opened it, or `fallback` when the page
 * was reached some other way (a bookmark, a pasted link, a reload of a stale entry).
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    const target = resolveReturn(location.state, fallback);
    navigate(target.to, { state: target.state });
  }, [navigate, location.state, fallback]);
}
